/**
 * Zod Runtime Validation Schemas
 *
 * These provide runtime validation for the JSON schema types.
 * Use these to validate external data at runtime.
 */

import { z } from 'zod';
import { JSON_TYPES, VALIDATION_MODES, INPUT_TYPES, NUMBER_LINE_STYLES, NUMBER_LINE_LABELS, NUMBER_LINE_HIGHLIGHT_COLORS, NUMBER_LINE_DRAG_MODES } from '../constants';
import { VISUAL_FRACTION_MAX_DIVISIONS, validateVisualFractionCapacity, CLOCK_TIME_REGEX } from '../ast/inline';

// ═══════════════════════════════════════════════════════════════════════════
// Layout Schemas
// ═══════════════════════════════════════════════════════════════════════════

export const TextItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.TEXT),
  value: z.string(),
});

export const QuestionTextSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.QUESTION_TEXT),
  value: z.string(),
});

export const WidgetRefSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.WIDGET_REF),
  widgetId: z.string(),
});

export const HighlightedTextSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.HIGHLIGHTED_TEXT),
  value: z.string(),
  highlight: z.tuple([z.number(), z.number()]).optional(),
});

export const ColorTextSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.COLOR_TEXT),
  value: z.string(),
  color: z.string(),
  highlight: z.tuple([z.number(), z.number()]).optional(),
});

export const BoldTextSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.BOLD_TEXT),
  value: z.string(),
  highlight: z.tuple([z.number(), z.number()]).optional(),
});

const IMAGE_SRC_SCHEME_RE = /^(https?:\/\/|data:image\/)/i;

export const ImageItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.IMAGE),
  name: z.string().min(1),
  src: z.string().url().refine((s) => IMAGE_SRC_SCHEME_RE.test(s), {
    message: 'src must use http(s) or data:image scheme',
  }),
  alt: z.string(),
  width: z.number().positive().optional(),
  height: z.number().positive().optional(),
});

export const SpaceItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.SPACE),
});

export const LinebreakSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.LINEBREAK),
});

export const OperatorSchema = z.enum(['+', '-', '×', '÷', '=', '<', '>', '?']);

export const OperatorItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.OPERATOR),
  operator: OperatorSchema,
});

// ── Clock ────────────────────────────────────────────────────────────────────
const ClockTimeSchema = z.string().regex(CLOCK_TIME_REGEX);
const ClockFormatSchema = z.enum(['12h', '24h']);

const ClockDisplayItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.CLOCK),
  mode: z.literal('display'),
  clockType: z.enum(['analog', 'digital']),
  value: ClockTimeSchema,
  format: ClockFormatSchema.optional(),
  name: z.string().optional(),
});

const ClockInteractiveItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.CLOCK),
  mode: z.literal('interactive'),
  clockType: z.literal('analog'),
  value: ClockTimeSchema,
  format: ClockFormatSchema.optional(),
  snap: z.number().int().min(1).max(60).optional(),
  name: z.string(),
});

export const ClockItemSchema = z.discriminatedUnion('mode', [
  ClockDisplayItemSchema,
  ClockInteractiveItemSchema,
]);

// Recursive type for GroupItem and Table
export const LayoutItemSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    QuestionTextSchema,
    TextItemSchema,
    WidgetRefSchema,
    HighlightedTextSchema,
    ColorTextSchema,
    BoldTextSchema,
    ImageItemSchema,
    SpaceItemSchema,
    LinebreakSchema,
    GroupItemSchema,
    OperatorItemSchema,
    TableItemSchema,
    NumberLineItemSchema,
    VerticalOperationItemSchema,
    ClockItemSchema,
  ])
);

export const GroupItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.GROUP),
  items: z.array(LayoutItemSchema),
});

// Table schemas
export const TableCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.TABLE_CELL),
  colspan: z.number().int().positive().optional(),
  items: z.array(LayoutItemSchema),
});

export const TableRowItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.TABLE_ROW),
  cells: z.array(TableCellItemSchema),
});

export const TableItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.TABLE),
  cols: z.number().int().positive(),
  rows: z.array(TableRowItemSchema),
});

// Number line schemas
export const NumberLinePositionItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.NUMBER_LINE_POSITION),
  value: z.number().finite(),
  content: z.array(LayoutItemSchema),
});

export const NumberLineCounterItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.NUMBER_LINE_COUNTER),
  min: z.number().int().min(1),
  max: z.number().int().positive(),
  default: z.number().int().nonnegative().optional(),
  name: z.string().optional(),
  answer: z.number().int().positive().optional(),
});

export const NumberLineHighlightItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.NUMBER_LINE_HIGHLIGHT),
  from: z.number().finite(),
  to: z.number().finite(),
  color: z.enum(NUMBER_LINE_HIGHLIGHT_COLORS).optional(),
  label: z.string().optional(),
});

export const NumberLineCursorItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.NUMBER_LINE_CURSOR),
  value: z.number().finite(),
  answer: z.number().finite(),
  color: z.string().regex(/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/).optional(),
  fillRange: z.boolean(),
  label: z.string().optional(),
});
export const NumberLineArrowItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.NUMBER_LINE_ARROW),
  from: z.number().finite(),
  to: z.number().finite(),
  color: z.string().optional(),
  content: z.array(LayoutItemSchema),
});

export const NumberLineItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.NUMBER_LINE),
  min: z.number().finite(),
  max: z.number().finite(),
  step: z.number().positive(),
  minorTicks: z.number().int().nonnegative().optional(),
  style: z.enum(NUMBER_LINE_STYLES).optional(),
  labelVisibility: z.enum(NUMBER_LINE_LABELS).optional(),
  counter: NumberLineCounterItemSchema.optional(),
  highlights: z.array(NumberLineHighlightItemSchema).optional(),
  cursors: z.array(NumberLineCursorItemSchema).optional(),
  arrows: z.array(NumberLineArrowItemSchema).optional(),
  dragMode: z.enum(NUMBER_LINE_DRAG_MODES).optional(),
  positions: z.array(NumberLinePositionItemSchema),
});

// Matcher schemas
export const MatcherItemContentSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.MATCHER_ITEM),
  /** Item identifier (no pipe allowed) */
  itemId: z.string().min(1).regex(/^[^|]+$/),
  /** Display-only inline content */
  content: z.array(LayoutItemSchema),
});

export const MatcherRowItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.MATCHER_ROW),
  items: z.array(MatcherItemContentSchema).min(1),
});

// Vertical operation schemas
export const VopOperatorJSONSchema = z.enum(['+', '-', '×', '÷', '=']);

export const VopDigitCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CELL),
  kind: z.literal('digit'),
  value: z.string(),
});

export const VopInputCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CELL),
  kind: z.literal('input'),
  widgetId: z.string(),
});

export const VopLabelCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CELL),
  kind: z.literal('label'),
  value: z.string(),
});

export const VopEmptyCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CELL),
  kind: z.literal('empty'),
});

export const VopColorCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CELL),
  kind: z.literal('color'),
  value: z.string(),
  color: z.string(),
});

export const VopBoldCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CELL),
  kind: z.literal('bold'),
  value: z.string(),
});

export const VopHighlightCellItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CELL),
  kind: z.literal('highlight'),
  value: z.string(),
});

export const VopCellItemSchema = z.discriminatedUnion('kind', [
  VopDigitCellItemSchema,
  VopLabelCellItemSchema,
  VopColorCellItemSchema,
  VopBoldCellItemSchema,
  VopHighlightCellItemSchema,
  VopInputCellItemSchema,
  VopEmptyCellItemSchema,
]);

export const VopRowItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_ROW),
  cells: z.array(VopCellItemSchema),
  op: VopOperatorJSONSchema.optional(),
});

export const VopCarryRowItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_CARRY_ROW),
  cells: z.array(VopCellItemSchema),
});

export const VopSeparatorItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VOP_SEPARATOR),
});

export const VerticalOperationItemSchema = z.object({
  id: z.string(),
  type: z.literal(JSON_TYPES.VERTICAL_OPERATION),
  op: VopOperatorJSONSchema.optional(),
  cols: z.number().int().positive(),
  items: z.array(z.union([VopRowItemSchema, VopCarryRowItemSchema, VopSeparatorItemSchema])),
});

// ═══════════════════════════════════════════════════════════════════════════
// Widget Schemas
// ═══════════════════════════════════════════════════════════════════════════

export const StringInputJSONSchema = z.object({
  type: z.literal(JSON_TYPES.INLINE_INPUT),
  inputType: z.literal(INPUT_TYPES.STRING),
  length: z.number().int().positive(),
  placeholder: z.string().max(32).optional(),
});

export const NumericInputJSONSchema = z.object({
  type: z.literal(JSON_TYPES.INLINE_INPUT),
  inputType: z.literal(INPUT_TYPES.NUMERIC),
  digits: z.number().int().positive(),
  placeholder: z.string().max(32).optional(),
});

export const InlineInputJSONSchema = z.discriminatedUnion('inputType', [
  StringInputJSONSchema,
  NumericInputJSONSchema,
]);

export const DragOptionsJSONSchema = z.object({
  type: z.literal(JSON_TYPES.DRAG_OPTIONS),
  options: z.array(z.string()),
});

// NOTE: Only `value` is stored — it serves as both the drop payload and the
// displayed label. If a future case needs the displayed text to differ from
// the drop value (e.g. rich/formatted labels, or a stable opaque id), a
// separate `label` (display) and `id` (payload) field should be added here.
export const InlineDragOptionJSONSchema = z.object({
  type: z.literal(JSON_TYPES.INLINE_DRAG_OPTION),
  value: z.string().min(1),
});

export const RadioOptionsJSONSchema = z.object({
  type: z.literal(JSON_TYPES.RADIO_OPTIONS),
  options: z.array(z.string()),
});

export const NumericKeypadJSONSchema = z.object({
  type: z.literal(JSON_TYPES.NUMERIC_KEYPAD),
  includeOperators: z.boolean().optional(),
  includeDecimal: z.boolean().optional(),
  style: z.enum(['diamond', 'classic']).optional(),
});

export const FractionJSONSchema = z.object({
  type: z.literal(JSON_TYPES.FRACTION),
  numerator: z.number().nullable(),
  denominator: z.number().nullable(),
  simplified: z.boolean(),
  placeholder: z.string().max(32).optional(),
});
/**
 * Matcher widget — rows of items, keyed by name in the widgets record.
 * The name is the key in the widgets record (like radio-options).
 * Rows/items carry IDs for renderer React keys.
 */
export const MatcherWidgetJSONSchema = z.object({
  type: z.literal(JSON_TYPES.MATCHER),
  rows: z.array(MatcherRowItemSchema).min(1),
  /** Optional color palette for the renderer (one color per answer group) */
  colors: z.array(z.string()).optional(),
  /** Number of answer groups — equals answers.length, stored here to avoid prop-drilling. */
  groupCount: z.number().int().min(1),
});

/**
 * Pair-Matcher widget — top row ↔ bottom row pairing.
 * topItems / bottomItems carry IDs for React keys.
 */
export const PairMatcherWidgetJSONSchema = z.object({
  type: z.literal(JSON_TYPES.PAIR_MATCHER),
  topItems: z.array(MatcherItemContentSchema).min(1),
  bottomItems: z.array(MatcherItemContentSchema).min(1),
});

export const VisualFractionJSONSchema = z.object({
  type: z.literal(JSON_TYPES.VISUAL_FRACTION),
  shape: z.enum(['bar', 'circle', 'triangle', 'grid']).default('bar'),
  divisions: z.number().int().positive().max(VISUAL_FRACTION_MAX_DIVISIONS, {
    message: `divisions cannot exceed ${VISUAL_FRACTION_MAX_DIVISIONS}`,
  }),
  filled: z.array(z.number().int().nonnegative()),
  answer: z.array(z.number().int().nonnegative()),
  orientation: z.enum(['horizontal', 'vertical']).optional(),
  /** Optional label text rendered near the shape — replaces the old showLabel boolean */
  label: z.string().optional(),
  /** Overall shape size in unified author units.
   * - bar / grid:         overrides `--visual-fraction-part-size` CSS custom property (cell size in rem).
   * - circle / triangle:  overrides `--visual-fraction-size` through the renderer scale factor.
   */
  size: z.number().positive().optional(),
}).superRefine(validateVisualFractionCapacity);

export const RulerWidgetSchema = z.object({
  type: z.literal(JSON_TYPES.RULER),
  size: z.number().optional(),
});

export const ProtractorWidgetSchema = z.object({
  type: z.literal(JSON_TYPES.PROTRACTOR),
  size: z.number().optional(),
});

export const SetSquareWidgetSchema = z.object({
  type: z.literal(JSON_TYPES.SET_SQUARE),
  size: z.number().optional(),
});

export const WidgetJSONSchema = z.union([
  InlineInputJSONSchema,
  DragOptionsJSONSchema,
  InlineDragOptionJSONSchema,
  RadioOptionsJSONSchema,
  NumericKeypadJSONSchema,
  FractionJSONSchema,
  MatcherWidgetJSONSchema,
  PairMatcherWidgetJSONSchema,
  VisualFractionJSONSchema,
  RulerWidgetSchema,
  ProtractorWidgetSchema,
  SetSquareWidgetSchema,
]);

// ═══════════════════════════════════════════════════════════════════════════
// Validation Schema
// ═══════════════════════════════════════════════════════════════════════════

export const ValidationJSONSchema = z.object({
  mode: z.enum([VALIDATION_MODES.SIMPLE, VALIDATION_MODES.EXPRESSION, VALIDATION_MODES.MIXED]),
  // Simple answers: string|number per widget id.
  // Matcher answers: string[][] keyed by matcher name. Outer/inner min(1)
  // mirrors MatcherBlockSchema invariants in src/ast/matcher.ts.
  // VisualFraction answers: number[] — one count per color group.
  answers: z.record(
    z.union([
      z.string(),
      z.number(),
      z.array(z.array(z.string().min(1)).min(1)).min(1),
      z.array(z.number().int().nonnegative()),
    ])
  ).optional(),
  expression: z.string().optional(),
  variables: z.array(z.string()).optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// Metadata Schema
// ═══════════════════════════════════════════════════════════════════════════

export const ExerciseMetaSchema = z.object({
  createdAt: z.string().optional(),
  author: z.string().optional(),
  tags: z.array(z.string()).optional(),
  difficulty: z.number().optional(),
});

// ═══════════════════════════════════════════════════════════════════════════
// Main Exercise Schema
// ═══════════════════════════════════════════════════════════════════════════

export const ExerciseJSONSchema = z.object({
  version: z.literal('2.0'),
  id: z.string().optional(),
  layout: z.array(LayoutItemSchema),
  widgets: z.record(WidgetJSONSchema),
  validation: ValidationJSONSchema.optional(),
  meta: ExerciseMetaSchema.optional(),
});

export type ValidatedExerciseJSON = ExerciseJSON & { validation: ValidationJSON };

// ═══════════════════════════════════════════════════════════════════════════
// Helper Functions
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Validate an exercise JSON object at runtime
 *
 * @param data - The data to validate
 * @returns Parsed and validated exercise JSON
 * @throws ZodError if validation fails
 *
 * @example
 * ```typescript
 * try {
 *   const validated = validateExerciseJSON(unknownData);
 *   console.log('Valid exercise:', validated);
 * } catch (error) {
 *   console.error('Validation failed:', error);
 * }
 * ```
 */
export function validateExerciseJSON(data: unknown) {
  return ExerciseJSONSchema.parse(data);
}

/**
 * Safely validate an exercise JSON object at runtime
 *
 * @param data - The data to validate
 * @returns Success result with data or error result
 *
 * @example
 * ```typescript
 * const result = validateExerciseJSONSafe(unknownData);
 * if (result.success) {
 *   console.log('Valid exercise:', result.data);
 * } else {
 *   console.error('Validation errors:', result.error.errors);
 * }
 * ```
 */
export function validateExerciseJSONSafe(data: unknown) {
  return ExerciseJSONSchema.safeParse(data);
}

/**
 * Validate a widget JSON object at runtime
 *
 * @param data - The data to validate
 * @returns Parsed and validated widget JSON
 * @throws ZodError if validation fails
 */
export function validateWidgetJSON(data: unknown) {
  return WidgetJSONSchema.parse(data);
}

/**
 * Safely validate a widget JSON object at runtime
 *
 * @param data - The data to validate
 * @returns Success result with data or error result
 */
export function validateWidgetJSONSafe(data: unknown) {
  return WidgetJSONSchema.safeParse(data);
}

// ═══════════════════════════════════════════════════════════════════════════
// Inferred TypeScript Types
// ═══════════════════════════════════════════════════════════════════════════

// Layout Types
export type QuestionText = z.infer<typeof QuestionTextSchema>;
export type TextItem = z.infer<typeof TextItemSchema>;
export type WidgetRef = z.infer<typeof WidgetRefSchema>;
export type HighlightedText = z.infer<typeof HighlightedTextSchema>;
export type ColorText = z.infer<typeof ColorTextSchema>;
export type BoldText = z.infer<typeof BoldTextSchema>;
export type ImageItem = z.infer<typeof ImageItemSchema>;
export type SpaceItem = z.infer<typeof SpaceItemSchema>;
export type Linebreak = z.infer<typeof LinebreakSchema>;
export type OperatorItem = z.infer<typeof OperatorItemSchema>;
export type GroupItem = z.infer<typeof GroupItemSchema>;
export type TableCellItem = z.infer<typeof TableCellItemSchema>;
export type TableRowItem = z.infer<typeof TableRowItemSchema>;
export type TableItem = z.infer<typeof TableItemSchema>;
export type NumberLinePositionItem = z.infer<typeof NumberLinePositionItemSchema>;
export type NumberLineCounterItem = z.infer<typeof NumberLineCounterItemSchema>;
export type NumberLineHighlightItem = z.infer<typeof NumberLineHighlightItemSchema>;
export type NumberLineCursorItem = z.infer<typeof NumberLineCursorItemSchema>;
export type NumberLineArrowItem = z.infer<typeof NumberLineArrowItemSchema>;
export type NumberLineItem = z.infer<typeof NumberLineItemSchema>;
export type MatcherItemContent = z.infer<typeof MatcherItemContentSchema>;
export type MatcherRowItem = z.infer<typeof MatcherRowItemSchema>;
export type MatcherWidgetJSON = z.infer<typeof MatcherWidgetJSONSchema>;
export type PairMatcherWidgetJSON = z.infer<typeof PairMatcherWidgetJSONSchema>;
export type VopDigitCellItem = z.infer<typeof VopDigitCellItemSchema>;
export type VopLabelCellItem = z.infer<typeof VopLabelCellItemSchema>;
export type VopInputCellItem = z.infer<typeof VopInputCellItemSchema>;
export type VopEmptyCellItem = z.infer<typeof VopEmptyCellItemSchema>;
export type VopColorCellItem = z.infer<typeof VopColorCellItemSchema>;
export type VopBoldCellItem = z.infer<typeof VopBoldCellItemSchema>;
export type VopHighlightCellItem = z.infer<typeof VopHighlightCellItemSchema>;
export type VopCellItem = z.infer<typeof VopCellItemSchema>;
export type VopRowItem = z.infer<typeof VopRowItemSchema>;
export type VopCarryRowItem = z.infer<typeof VopCarryRowItemSchema>;
export type VopSeparatorItem = z.infer<typeof VopSeparatorItemSchema>;
export type VerticalOperationItem = z.infer<typeof VerticalOperationItemSchema>;
export type ClockItem = z.infer<typeof ClockItemSchema>;
export type LayoutItem = z.infer<typeof LayoutItemSchema>;

// Widget Types
export type InlineInputJSON = z.infer<typeof InlineInputJSONSchema>;
export type DragOptionsJSON = z.infer<typeof DragOptionsJSONSchema>;
export type InlineDragOptionJSON = z.infer<typeof InlineDragOptionJSONSchema>;
export type RadioOptionsJSON = z.infer<typeof RadioOptionsJSONSchema>;
export type NumericKeypadJSON = z.infer<typeof NumericKeypadJSONSchema>;
export type FractionJSON = z.infer<typeof FractionJSONSchema>;
export type VisualFractionJSON = z.infer<typeof VisualFractionJSONSchema>;
export type RulerWidgetJSON = z.infer<typeof RulerWidgetSchema>;
export type ProtractorWidgetJSON = z.infer<typeof ProtractorWidgetSchema>;
export type SetSquareWidgetJSON = z.infer<typeof SetSquareWidgetSchema>;
export type WidgetJSON = z.infer<typeof WidgetJSONSchema>;

// Validation Types
export type ValidationJSON = z.infer<typeof ValidationJSONSchema>;

// Meta Types
export type ExerciseMeta = z.infer<typeof ExerciseMetaSchema>;

// Main Exercise Type
export type ExerciseJSON = z.infer<typeof ExerciseJSONSchema>;
